import React, { useEffect } from 'react';
import { Button } from './Button';

interface LandingPageProps {
  onLogin: () => void;
  onSignup: () => void;
  onTryDemo: () => void;
  onBlogClick?: () => void;
  onPricingClick?: () => void;
  onPrivacyClick?: () => void;
  onTermsClick?: () => void;
  onContactClick?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ 
    onLogin, 
    onSignup, 
    onTryDemo,
    onBlogClick, 
    onPricingClick,
    onPrivacyClick, 
    onTermsClick,
    onContactClick
}) => {
  
  // SEO: Reset title when on Landing Page
  useEffect(() => {
    document.title = "LoveOnRead - Master the Art of Chat";
  }, []);

  return (
    <div className="bg-slate-50 min-h-full flex flex-col font-sans">
      
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-5xl mx-auto w-full">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
           <img 
             src="https://image2url.com/r2/default/images/1769369928623-3180bbac-a140-4e50-ac88-1cfcf218a72a.png" 
             alt="LoveOnRead Logo" 
             className="w-8 h-8 object-contain"
           />
           <span className="font-bold text-xl tracking-tight">
             <span className="text-love">Love</span>
             <span className="text-read">OnRead</span>
           </span>
        </div>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-6">
            {onBlogClick && (
                <button onClick={onBlogClick} className="text-slate-600 font-medium hover:text-brand-600 transition-colors text-sm">
                    Blog
                </button>
            )}
            {onPricingClick && (
                <button onClick={onPricingClick} className="text-slate-600 font-medium hover:text-brand-600 transition-colors text-sm">
                    Pricing
                </button>
            )}
        </div>

        <div className="flex items-center gap-3">
          <button onClick={onLogin} className="text-slate-600 font-medium hover:text-brand-600 transition-colors text-sm">
            Log in
          </button>
          <button onClick={onSignup} className="bg-slate-900 text-white px-4 py-2 rounded-full font-medium text-sm hover:bg-slate-800 transition-all shadow-md">
            Sign Up
          </button>
        </div>
      </nav>

      <main className="flex-1 w-full max-w-5xl mx-auto px-6 pt-8 pb-16">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in-up">
          
          {/* iOS App Announcement Pill */}
          <a 
            href="https://apps.apple.com/us/app/loveonread/id6758550556"
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-slate-900 text-white rounded-full text-[10px] font-bold mb-6 hover:bg-slate-700 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <span>🚀 New! iOS App Now Available</span>
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
               <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </a>

          <div className="block">
            <div className="inline-block px-3 py-1 bg-brand-50 border border-brand-200 rounded-full text-brand-700 text-xs font-bold mb-6 tracking-wide uppercase">
                Relationship Coaching AI
            </div>
          </div>

          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
            Master the Art of <span className="text-transparent bg-clip-text bg-gradient-to-r from-love to-read">Conversation</span>
          </h1>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-2xl mx-auto">
            Struggling to find the right words? Practice difficult conversations with our realistic AI scenarios. Build confidence and improve intimacy without the risk.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
            {/* Main Primary CTA is now the Demo */}
            <Button onClick={onTryDemo} className="px-8 py-4 text-lg w-full sm:w-auto shadow-xl hover:scale-105 transition-transform">
              Try Demo Instantly
            </Button>
            <button 
                onClick={onSignup}
                className="px-8 py-4 text-lg font-medium text-slate-700 bg-white border-2 border-slate-200 rounded-xl hover:border-brand-300 hover:text-brand-600 transition-colors w-full sm:w-auto"
            >
                Create Account
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-2 mb-8">
             <span className="text-green-600 font-bold">✓ No Login Required</span> for demo
          </p>

          {/* App Store Badge - Prominent */}
          <div className="flex flex-col items-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
             <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Or download on mobile</p>
             <a 
               href="https://apps.apple.com/us/app/loveonread/id6758550556" 
               target="_blank" 
               rel="noopener noreferrer"
               className="transition-transform hover:scale-105 hover:opacity-90 block"
             >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
                  alt="Download on the App Store" 
                  className="h-10 sm:h-12 w-auto"
                />
             </a>
          </div>
        </div>

        {/* Demo / Visuals Placeholder - Simulating a chat interface */}
        <div className="relative max-w-sm mx-auto mb-20 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="absolute -inset-1 bg-gradient-to-r from-love to-read rounded-[2rem] blur opacity-30"></div>
          <div className="relative bg-white border border-slate-200 rounded-[1.5rem] shadow-2xl overflow-hidden cursor-pointer" onClick={onTryDemo}>
             {/* Overlay prompting click */}
             <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/0 hover:bg-white/10 transition-colors group">
                <div className="bg-white/90 backdrop-blur px-4 py-2 rounded-full shadow-lg text-sm font-bold text-brand-600 group-hover:scale-110 transition-transform">
                    Click to Try Now
                </div>
             </div>

            <div className="bg-slate-50 border-b border-slate-100 p-4 flex items-center gap-3">
               <div className="w-8 h-8 rounded-full bg-slate-200"></div>
               <div className="h-4 w-24 bg-slate-200 rounded"></div>
            </div>
            <div className="p-4 space-y-4 pointer-events-none">
               {/* Simulated Chat */}
               <div className="flex justify-start">
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-none px-4 py-2 text-sm text-slate-800 shadow-sm max-w-[85%]">
                    I feel like we never really talk anymore. It's just... quiet.
                  </div>
               </div>
               <div className="flex justify-end">
                  <div className="bg-brand-600 text-white rounded-2xl rounded-tr-none px-4 py-2 text-sm shadow-sm max-w-[85%]">
                    I've noticed that too, and I miss our conversations. Can we set aside time tonight to catch up properly?
                  </div>
               </div>
               
               {/* Coaching Tip */}
               <div className="bg-green-50 border border-green-100 rounded-lg p-3 flex gap-2">
                  <div className="w-5 h-5 bg-green-200 rounded-full flex items-center justify-center text-[10px] font-bold text-green-700 flex-shrink-0">✓</div>
                  <div>
                    <p className="text-[10px] font-bold text-green-800 uppercase">Great Response</p>
                    <p className="text-xs text-green-700">You validated their feelings ("I noticed that too") and proposed an actionable solution.</p>
                  </div>
               </div>
            </div>
          </div>
        </div>

        {/* Features / Value Prop */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 text-2xl">🎭</div>
            <h3 className="font-bold text-slate-900 text-lg mb-2">Simulate Reality</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              This is not a fantasy bot. It's a training ground. Create scenarios like "Argument about money" or "Asking for a date" and practice safely.
            </p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4 text-2xl">🧠</div>
            <h3 className="font-bold text-slate-900 text-lg mb-2">Real-Time Coaching</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Get instant feedback on your texts. Our AI analyzes your tone, empathy, and clarity, giving you a score and actionable tips.
            </p>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-2xl">🔒</div>
            <h3 className="font-bold text-slate-900 text-lg mb-2">Private & Secure</h3>
            <p className="text-slate-500 text-sm leading-relaxed">
              Your relationship data is sensitive. Conversations are private, encrypted, and never sold. You own your data.
            </p>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-20">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-10">Real Results from Real People</h2>
          <div className="grid md:grid-cols-3 gap-6">
            
            {/* Testimonial 1: LDR */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col hover:-translate-y-1 transition-transform">
               <div className="flex items-center gap-1 text-yellow-400 mb-4">
                  {'★'.repeat(5)}
               </div>
               <p className="text-slate-600 text-sm leading-relaxed mb-6 italic flex-1">
                 "My boyfriend lives in London and I'm in Lagos. We were running out of things to say. This app gave me specific scenarios to practice, and now our FaceTime calls last hours again. It saved us."
               </p>
               <div className="flex items-center gap-3 mt-auto border-t border-slate-50 pt-4">
                 <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden border border-slate-200">
                    <img src="https://api.dicebear.com/9.x/micah/svg?seed=Sarah&backgroundColor=fdf4cd" alt="Sarah" className="w-full h-full object-cover" />
                 </div>
                 <div>
                    <p className="font-bold text-slate-900 text-sm">Sarah A.</p>
                    <p className="text-xs text-slate-500 font-medium bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full inline-block mt-0.5">Long Distance</p>
                 </div>
               </div>
            </div>

            {/* Testimonial 2: Dating */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col hover:-translate-y-1 transition-transform">
               <div className="flex items-center gap-1 text-yellow-400 mb-4">
                  {'★'.repeat(5)}
               </div>
               <p className="text-slate-600 text-sm leading-relaxed mb-6 italic flex-1">
                 "I used to get ghosted constantly after the first few messages. The AI coach showed me I was asking boring 'interview questions'. I practiced being more playful here first, and now I have 3 dates lined up for next week."
               </p>
               <div className="flex items-center gap-3 mt-auto border-t border-slate-50 pt-4">
                 <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden border border-slate-200">
                    <img src="https://api.dicebear.com/9.x/micah/svg?seed=Michael&backgroundColor=b6e3f4" alt="Michael" className="w-full h-full object-cover" />
                 </div>
                 <div>
                    <p className="font-bold text-slate-900 text-sm">Michael T.</p>
                    <p className="text-xs text-slate-500 font-medium bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full inline-block mt-0.5">Dating Apps</p>
                 </div>
               </div>
            </div>

            {/* Testimonial 3: Conflict */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex flex-col hover:-translate-y-1 transition-transform">
               <div className="flex items-center gap-1 text-yellow-400 mb-4">
                  {'★'.repeat(5)}
               </div>
               <p className="text-slate-600 text-sm leading-relaxed mb-6 italic flex-1">
                 "I usually freeze up when my partner is upset. I practiced the 'Angry Partner' scenario 10 times until I stopped panicking. When a real fight happened yesterday, I stayed calm and we actually solved it. Unreal."
               </p>
               <div className="flex items-center gap-3 mt-auto border-t border-slate-50 pt-4">
                 <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden border border-slate-200">
                    <img src="https://api.dicebear.com/9.x/micah/svg?seed=Jessica&backgroundColor=ffdfbf" alt="Jessica" className="w-full h-full object-cover" />
                 </div>
                 <div>
                    <p className="font-bold text-slate-900 text-sm">Jessica K.</p>
                    <p className="text-xs text-slate-500 font-medium bg-red-50 text-red-700 px-2 py-0.5 rounded-full inline-block mt-0.5">Relationship Anxiety</p>
                 </div>
               </div>
            </div>

          </div>
        </div>

        {/* CTA */}
        <div className="text-center mb-16">
           <h2 className="text-2xl font-bold text-slate-900 mb-4">Ready to improve your connections?</h2>
           <Button onClick={onTryDemo} className="px-10 py-3 shadow-lg hover:shadow-xl hover:-translate-y-1">
             Try It Now (No Login)
           </Button>
        </div>
      </main>

      {/* Footer with Trust Signals */}
      <footer className="bg-white border-t border-slate-100 py-8 px-6">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
          <div>
            &copy; {new Date().getFullYear()} LoveOnRead. All rights reserved.
          </div>
          <div className="flex gap-6 items-center flex-wrap justify-center">
            {onPricingClick && <button className="hover:text-brand-600 transition-colors font-semibold" onClick={onPricingClick}>Pricing</button>}
            {onBlogClick && <button className="hover:text-brand-600 transition-colors font-semibold" onClick={onBlogClick}>Blog</button>}
            
            <button 
                className="hover:text-slate-800 transition-colors" 
                onClick={onPrivacyClick || (() => {})}
            >
                Privacy Policy
            </button>
            
            <button 
                className="hover:text-slate-800 transition-colors" 
                onClick={onTermsClick || (() => {})}
            >
                Terms of Service
            </button>
            
            <button 
                className="hover:text-slate-800 transition-colors" 
                onClick={onContactClick || (() => window.location.href = 'mailto:loveonreadai@gmail.com')}
            >
                Contact Support
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};